import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AuthShell } from '@/components/AuthShell';
import { Alert } from '@/components/ui/Alert';
import { buttonVariants } from '@/components/ui/Button';
import { Loader2 } from '@/components/icons';
import { authApi } from '@/features/auth/api';
import { useRefreshViewer } from '@/features/auth/AuthContext';

type State = 'idle' | 'verifying' | 'success' | 'error';

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const refreshViewer = useRefreshViewer();
  const [state, setState] = useState<State>(token ? 'verifying' : 'idle');
  const ran = useRef(false);

  useEffect(() => {
    if (!token || ran.current) return;
    ran.current = true;
    authApi
      .verifyEmail(token)
      .then(() => {
        setState('success');
        void refreshViewer();
      })
      .catch(() => setState('error'));
  }, [token, refreshViewer]);

  return (
    <AuthShell
      title="Verify your email"
      footer={
        <Link to="/browse" className="font-semibold text-brand-soft hover:text-brand">
          Go to BingeBox
        </Link>
      }
    >
      {state === 'idle' && (
        <Alert tone="info">
          Check your inbox for a verification link. You can keep browsing in the meantime — verify
          when you're ready.
        </Alert>
      )}
      {state === 'verifying' && (
        <div className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 className="animate-spin" size={18} /> Verifying your email…
        </div>
      )}
      {state === 'success' && (
        <div className="space-y-4">
          <Alert tone="success">Your email is verified. You're all set.</Alert>
          <Link to="/browse" className={buttonVariants({ size: 'lg', className: 'w-full' })}>
            Start watching
          </Link>
        </div>
      )}
      {state === 'error' && (
        <Alert tone="error">This verification link is invalid or has expired.</Alert>
      )}
    </AuthShell>
  );
}
